import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CoordInput } from '@/components/homepage/CoordInput'
import { MyLocationButton } from '@/components/site/MyLocationButton'
import { ResultsPanel } from '@/components/homepage/ResultsPanel'
import { AttributionPill } from '@/components/homepage/AttributionPill'
import { SurveyBorderCheckbox } from '@/components/SurveyBorderCheckbox'
import { Map, type MapHandle } from '@/components/homepage/Map'
import { geomBounds } from '@/lib/geo'
import { BasemapToggle } from '@/components/BasemapToggle'
import { activeBasemapProvider } from '@/lib/map'
import { useTheme } from '@/lib/theme'
import { useBasemap } from '@/lib/basemap'
import {
  findHit,
  geocode,
  type LocationResult,
} from '@/lib/api/location'
import { writeCoordToUrl } from '@/lib/coord-url'
import { initialMapView, saveMapView } from '@/lib/map-view'
import { normalizeCoord } from '@/lib/geo'
import { formatCircuit, formatJudicialDistrict } from '@/lib/judicial-format'

const DEMO_CENTER: [number, number] = [-110.8955419, 32.1940926]
const COORD_PRECISION = 4

function fmt(n: number): string {
  return n.toFixed(COORD_PRECISION)
}

// Default to a regional view so the survey U.S. border is visible on first
// load; a shared `?z=` / a stored view overrides it. The initial view is
// resolved per-mount inside the component (initialMapView) so navigating back
// to this page re-reads the URL + stored view instead of a stale module const.
const HOME_DEFAULT_ZOOM = 7

interface PanelRow {
  k: string
  v: string
  geoborder_id?: string | null
}

interface PanelData {
  rows: PanelRow[]
  distance:
    | { meters: number; miles: number; direction: string | null; bearing_degrees: number | null; label: string }
    | null
  /** Bottom-of-panel "Federal Land" row. Only present when the
   * federal_land synth hit was returned for this coordinate — otherwise
   * we don't render a status row at all (no "Private Land" line). */
  landStatus: { detail: string | null; href?: string | null } | null
}

// IDs of layers that, when present in the API response, count toward
// the "federal land" status. Per-agency hits supply the detail string
// ("Tonto National Forest" etc.); the federal_land synth is the
// presence flag. Judicial / immigration federal_* layers are
// deliberately excluded — they're federal *jurisdiction*, not
// federal *land ownership*.
const FEDERAL_LAND_LAYERS = new Set([
  'federal_blm',
  'federal_usfs',
  'federal_nps',
  'federal_fws',
  'federal_usace',
  'federal_usbr',
  'federal_doe',
  'federal_military',
  'federal_bia_trust',
  'federal_roosevelt_reservation',
])

const LAYER_LABEL: Record<string, string> = {
  country: 'Country',
  state: 'State',
  county: 'County',
  place: 'City',
  // Public-facing label uses "Zip" — what most users call it. The
  // /data page keeps the technically-accurate "ZCTA" (ZIP Code
  // Tabulation Area) with full source + caveats.
  zcta: 'Zip',
  congressional_district: 'Congressional',
  cbsa: 'Metro Area',
  federal_circuit_court: 'Circuit Court',
  federal_judicial_district: 'District Court',
  federal_judicial_division: 'District Court Division',
  federal_immigration_court: 'Immigration Court',
  ice_ero_field_office: 'ICE Field Office',
  federal_port_of_entry: 'Port of Entry',
  federal_blm: 'Public Lands (BLM)',
  federal_usfs: 'Forest Service (USFS)',
  federal_nps: 'National Park / Monument (NPS)',
  federal_fws: 'Wildlife Refuge (FWS)',
  federal_usace: 'Army Corps (USACE)',
  federal_usbr: 'Reclamation (USBR)',
  federal_doe: 'Energy (DOE)',
  federal_military: 'Military (DoW)',
  federal_bia_trust: 'Tribal Trust (BIA)',
  federal_roosevelt_reservation: 'Roosevelt Reservation',
  federal_land: 'Federal Land',
}

// Layers that are returned by the API but suppressed on the homepage
// map. The Explore page surfaces these (off by default; user opts in
// via the layer toggle). Keeps the homepage focused on the broad
// civic / judicial layers without specialized administrative rows.
const HOMEPAGE_HIDDEN_LAYERS = new Set([
  // Metro Area (CBSA) gets squeezed out on the homepage when a Court
  // Division + Federal Land row both render — and CBSA is the least
  // load-bearing civic anchor (Country/State/County/City already
  // cover "where am I?"). Explore still surfaces it via the Local
  // group toggle.
  'cbsa',
  'federal_immigration_court',
  'ice_ero_field_office',
  'federal_port_of_entry',
  'federal_blm',
  'federal_usfs',
  'federal_nps',
  'federal_fws',
  'federal_usace',
  'federal_usbr',
  'federal_doe',
  'federal_military',
  'federal_bia_trust',
  'federal_roosevelt_reservation',
  'federal_land',
])

function buildPanel(result: LocationResult): PanelData {
  // International waters: the click landed outside every country polygon.
  // Surface the named nearest country + direction + distance instead of
  // a blank panel.
  if (result.hits.length === 0 && result.nearest_country) {
    const nc = result.nearest_country
    return {
      rows: [{ k: 'Location', v: 'International waters' }],
      distance: {
        meters: nc.distance_meters,
        miles: nc.distance_miles,
        direction: nc.cardinal,
        bearing_degrees: nc.bearing_degrees,
        label: `nearest land: ${nc.name === 'United States' ? 'U.S.' : nc.name}`,
      },
      landStatus: null,
    }
  }

  // Heading + sub block (county/state title + country sub-heading) were
  // removed — the rows below already show Country / State / County /
  // City / etc., so the heading was strictly duplicative.
  const country = findHit(result, 'country')

  const rows: PanelRow[] = []
  for (const hit of result.hits) {
    if (HOMEPAGE_HIDDEN_LAYERS.has(hit.layer_id)) continue
    // federal_circuit_court is its own layer now (PR #11). Use the short
    // "9th Circuit" / "D.C. Circuit" form for the value rather than the
    // long source name "United States Court of Appeals for the…".
    if (hit.layer_id === 'federal_circuit_court') {
      const v = formatCircuit(hit.identifiers.circuit as string | undefined) ?? hit.name
      rows.push({ k: 'Circuit Court', v, geoborder_id: hit.geoborder_id })
      continue
    }
    // federal_judicial_district now emits only the District Court line;
    // the Circuit Court row above is driven by the dedicated layer hit.
    if (hit.layer_id === 'federal_judicial_district') {
      rows.push({
        k: 'District Court',
        v: formatJudicialDistrict(hit.identifiers as Record<string, unknown>),
        geoborder_id: hit.geoborder_id,
      })
      continue
    }
    const label = LAYER_LABEL[hit.layer_id] ?? hit.layer_id
    // Homepage drops the FIPS / ISO code suffix entirely — Explore is where
    // the codes appear (in parens). Flag the U.S. country row so the
    // survey-grade in-country result reads at a glance without a separate line.
    const value =
      hit.layer_id === 'country' && hit.name === 'United States'
        ? `🇺🇸 ${hit.name}`
        : hit.name
    rows.push({ k: label, v: value, geoborder_id: hit.geoborder_id })
  }

  let distance: PanelData['distance'] = null
  if (country) {
    // Append "with X" when we know what's on the other side of the closest
    // border edge: "United States border with Mexico" / "with Canada" /
    // "with water" (coastline, Great Lakes, etc.). border_adjacent is null
    // for points on the boundary line itself, in which case we just say
    // "United States border" — the suffix would add nothing useful.
    const adj = country.border_adjacent
    // Abbreviate "United States" → "U.S." in the border callout so the
    // line "59.3 mi · S · United States border with Mexico" stays inside
    // the result panel without wrapping. Other country names pass through.
    const countryShort = country.name === 'United States' ? 'U.S.' : country.name
    let label = `${countryShort} border`
    if (adj?.kind === 'country' && adj.name) {
      // Same abbreviation on the *adjacent* side — "with United
      // States" → "with U.S." — to stay consistent with the
      // countryShort treatment above (and the international-waters
      // path on line 73). Most-relevant when the current country is
      // Mexico or Canada and the border callout reads "Mexico border
      // with U.S." rather than "with United States".
      const adjShort = adj.name === 'United States' ? 'U.S.' : adj.name
      label += ` with ${adjShort}`
    }
    else if (adj?.kind === 'water') label += ' with water'
    distance = {
      meters: country.nearest_boundary.distance_meters,
      miles: country.nearest_boundary.distance_miles,
      direction: country.nearest_boundary.cardinal,
      bearing_degrees: country.nearest_boundary.bearing_degrees,
      label,
    }
  }

  // Bottom-of-panel "Federal Land" row — only when the federal_land
  // synth hit was emitted by geocode_internal (i.e. the point lies
  // inside at least one agency-specific federal_land layer). detail
  // surfaces the most specific federal-land row's name (e.g. "Tonto
  // National Forest") so users see WHICH agency owns/manages the
  // parcel; href deep-links to Explore's Public Lands group at the
  // same coordinate.
  const isFederal = result.hits.some((h) => h.layer_id === 'federal_land')
  const federalDetail = result.hits.find((h) => FEDERAL_LAND_LAYERS.has(h.layer_id))
  const landStatus = isFederal
    ? {
        detail: federalDetail?.name ?? null,
        href: `/explore?lat=${result.query.lat}&lon=${result.query.lon}&group=federal_land`,
      }
    : null

  return { rows, distance, landStatus }
}

interface LookupOptions {
  /**
   * If true, fly the map to fit the smallest containing boundary's bounds
   * once the result arrives. Used by the Search button so typed coords
   * land at a sensible zoom (county-level, state-level, etc.) instead of
   * staying at whatever the current zoom happens to be.
   */
  fitToResult?: boolean
}

export function HeroMap() {
  const { theme } = useTheme()
  const { kind } = useBasemap()
  // Resolve the initial view once per mount: shared URL → stored view → default.
  const initial = useMemo(() => initialMapView(DEMO_CENTER, HOME_DEFAULT_ZOOM), [])
  const [lat, setLat] = useState<string>(fmt(initial.center[1]))
  const [lon, setLon] = useState<string>(fmt(initial.center[0]))
  const [result, setResult] = useState<LocationResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hoveredGeoborderId, setHoveredGeoborderId] = useState<string | null>(null)
  const [showSurveyBorder, setShowSurveyBorder] = useState<boolean>(true)
  const mapRef = useRef<MapHandle>(null)
  const mapZoomRef = useRef<number>(initial.zoom)
  const requestSeq = useRef(0)
  const { attribution } = activeBasemapProvider.getStyle({ theme, kind })

  const lookup = useCallback(async (lonN: number, latN: number, opts: LookupOptions = {}) => {
    const seq = ++requestSeq.current
    writeCoordToUrl(lonN, latN, mapZoomRef.current)
    saveMapView(lonN, latN, mapZoomRef.current)
    setLoading(true)
    setError(null)
    // Clear any pinned hover-highlight from a previous coordinate — the
    // hits list is about to change, so the old geoborder_id may no
    // longer correspond to anything on screen.
    setHoveredGeoborderId(null)
    try {
      // Strict mode: use the survey-grade definitive border so border-sliver
      // clicks resolve to the correct country (no coarse "Mexico" row a few
      // feet inside the U.S.) and the U.S. country row / flag stays consistent.
      const data = await geocode(lonN, latN, { strictUsBorder: true })
      if (seq !== requestSeq.current) return
      setResult(data)
      // Smart zoom: after Search, fit the smallest containing
      // polygon's bounds. Hits come back ordered by sort_order
      // ascending (country → state → county → …). Point layers
      // (immigration court, ICE field office) now also live in
      // hits with HIGHER sort_order than polygons, so the literal
      // tail of the array is a Point — geomBounds returns null
      // for those. Walk backward and pick the last polygon hit.
      if (opts.fitToResult && data.hits.length > 0) {
        const smallest = [...data.hits].reverse().find((h) => {
          const t = h.display_geom?.type
          return t === 'Polygon' || t === 'MultiPolygon'
        })
        if (smallest?.display_geom) {
          const bounds = geomBounds(smallest.display_geom as unknown as GeoJSON.Geometry)
          if (bounds) mapRef.current?.fitBounds(bounds)
        }
      }
    } catch (e) {
      if (seq !== requestSeq.current) return
      setError(e instanceof Error ? e.message : 'Failed to look up coordinate')
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }, [])

  const onPinDrag = useCallback(([lonN, latN]: [number, number]) => {
    setLat(fmt(latN))
    setLon(fmt(lonN))
  }, [])

  const onPinCommit = useCallback(
    ([lonN, latN]: [number, number]) => {
      setLat(fmt(latN))
      setLon(fmt(lonN))
      lookup(lonN, latN)
    },
    [lookup],
  )

  // Persist the map zoom into the URL (alongside lat/lon) so a shared link
  // restores the exact same view.
  const onZoomChange = useCallback(
    (z: number) => {
      mapZoomRef.current = z
      const lonN = Number.parseFloat(lon)
      const latN = Number.parseFloat(lat)
      if (Number.isFinite(lonN) && Number.isFinite(latN)) {
        writeCoordToUrl(lonN, latN, z)
        saveMapView(lonN, latN, z)
      }
    },
    [lat, lon],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void lookup(initial.center[0], initial.center[1])
  }, [lookup, initial])

  const onSearch = useCallback(() => {
    const latN = Number.parseFloat(lat)
    const lonN = Number.parseFloat(lon)
    if (!Number.isFinite(latN) || !Number.isFinite(lonN)) {
      setError('Lat / lon must be numbers.')
      return
    }
    // Wrap out-of-range coords instead of rejecting — a user typing
    // -189 (one hop past the antimeridian) probably means 171, not "no".
    const [wLon, wLat] = normalizeCoord(lonN, latN)
    setLat(fmt(wLat))
    setLon(fmt(wLon))
    mapRef.current?.flyTo([wLon, wLat])
    lookup(wLon, wLat, { fitToResult: true })
  }, [lat, lon, lookup])

  const onLocate = useCallback(
    ([lonN, latN]: [number, number]) => {
      setLat(fmt(latN))
      setLon(fmt(lonN))
      // flyTo zooms to street-level (10) — that's where the user wants
      // to see themselves. fitToResult is OFF on purpose: it would fit
      // to the smallest containing boundary (often a county / state),
      // which yanks the camera back out after the fly. User report:
      // "first it flies to the location but then when it loads the
      // map and info box it zooms out."
      mapRef.current?.flyTo([lonN, latN], 10)
      lookup(lonN, latN)
    },
    [lookup],
  )

  // Click a row to zoom to that boundary's extent + keep the polygon
  // highlighted while the camera animates. Without the explicit re-set,
  // browser focus shifts on the clicked <li role="button"> were firing
  // onBlur → setHoveredGeoborderId(null) mid-animation and clearing the
  // green fill the moment the user committed. Re-affirming hoveredId
  // here means the highlight survives the click; normal mouseleave /
  // hover-on-another-row clears it from there.
  const onRowClick = useCallback(
    (geoborderId: string) => {
      const hit = result?.hits.find((h) => h.geoborder_id === geoborderId)
      const geom = hit?.display_geom as GeoJSON.Geometry | null | undefined
      if (!geom) return
      if (geom.type === 'Point') {
        // Point hits (immigration courts, ICE field offices) — fly to
        // the point at city-scale zoom WITHOUT moving the user's pin.
        // The pin marks the input coordinate they're inspecting from;
        // dragging it along would change the lookup. fitBounds on a
        // single-point bbox would either no-op or zoom past usability,
        // so a fixed zoom level is what users expect here.
        const [lon, lat] = geom.coordinates as [number, number]
        mapRef.current?.flyTo([lon, lat], 12, { moveMarker: false })
      } else {
        const bounds = geomBounds(geom)
        if (bounds) mapRef.current?.fitBounds(bounds)
      }
      setHoveredGeoborderId(geoborderId)
    },
    [result],
  )

  // The result panel only renders when there's actually a result (or an
  // active error / load). Pre-result we don't show a "No location yet"
  // placeholder card.
  const panel: PanelData | null = useMemo(
    () => (result ? buildPanel(result) : null),
    [result],
  )

  // Same filter as buildPanel — drop the homepage-hidden point layers
  // from the map's display geometry so the dots don't render. Explore
  // is the only place users see these (off by default; toggle to opt
  // in).
  const visibleHits = useMemo(
    () => result?.hits.filter((h) => !HOMEPAGE_HIDDEN_LAYERS.has(h.layer_id)),
    [result],
  )

  return (
    <section
      aria-label="Coordinate map"
      // Mobile: compact 50vh (max 380, min 300) so a chunk of the
      // marketing content below the hero is in view without scrolling
      // on phones. Desktop / tablet (sm+): tall hero like before but
      // ~50 px shorter than the original to expose the start of the
      // section below.
      className={[
        'relative w-full border-b border-[var(--color-border)] overflow-hidden',
        'h-[min(55vh,420px)] min-h-[340px]',
        'sm:h-[min(calc(75vh-50px),670px)] sm:min-h-[430px]',
      ].join(' ')}
    >
      <Map
        ref={mapRef}
        center={initial.center}
        zoom={initial.zoom}
        onCoordChange={onPinDrag}
        onCoordCommit={onPinCommit}
        onZoomChange={onZoomChange}
        hits={visibleHits}
        hoveredGeoborderId={hoveredGeoborderId}
        showSurveyBorder={showSurveyBorder}
      />
      <CoordInput
        lat={lat}
        onLatChange={setLat}
        lon={lon}
        onLonChange={setLon}
        onSearch={onSearch}
      />
      {/* Top-right cluster: locate-me + basemap toggle. Single flex
          container so both buttons stay visible and aligned at every
          viewport. Previously MyLocationButton was positioned at a
          hard-coded left-[380px] which fell off-screen on phones. */}
      <div className="absolute right-3 top-3 z-20 flex items-center gap-2">
        <MyLocationButton onLocated={onLocate} />
        <BasemapToggle />
      </div>

      {(panel || error) && (
        <ResultsPanel
          rows={panel?.rows ?? []}
          distance={panel?.distance ?? null}
          landStatus={panel?.landStatus ?? null}
          loading={loading}
          error={error}
          onRowHover={setHoveredGeoborderId}
          onRowClick={onRowClick}
        />
      )}
      {/* Standalone survey-border toggle, bottom-left above the attribution —
          mirrors the checkbox in Explore's Layers panel. */}
      <div
        className={[
          'absolute left-5 bottom-12 z-20 px-3 py-2.5',
          'rounded-[var(--radius-lg)]',
          'bg-[var(--color-elev-strong)] backdrop-blur-[14px]',
          'border border-[var(--color-border)] shadow-[var(--shadow-coord)]',
        ].join(' ')}
      >
        <SurveyBorderCheckbox checked={showSurveyBorder} onChange={setShowSurveyBorder} />
      </div>
      <AttributionPill attribution={attribution} />
    </section>
  )
}
